interface PriceRange {
  maximumOrderPrice: string;
  minimumOrderPrice: string;
  priceAmount: string;
}
interface Status {
  status: string;
}
interface RateName {
  rateName?: string;
  name?: string;
}
interface DeliveryType {
  delivery_type: string;
}
interface RateNameDelType {
  name: string;
  deliveryType: string;
}

type ParamOldNew =
  | Partial<PriceRange>
  | Partial<Status>
  | Partial<RateName>
  | Partial<DeliveryType>
  | Partial<RateNameDelType>;

interface Parameter {
  old: ParamOldNew;
  new: ParamOldNew;
  action: string;
}

export interface PriceRateUpdate {
  rateName: string;
  deliveryType: string;
  newStatus: string;
  parameters?: Parameter[];
}

const isPriceRangeFields = (obj: any): obj is PriceRange =>
  typeof obj === "object" &&
  "maximumOrderPrice" in obj &&
  "minimumOrderPrice" in obj;

const isPriceAmountFields = (obj: any): obj is PriceRange =>
  typeof obj === "object" && "priceAmount" in obj;

export const hasPriceRangeChanged = (param: Parameter): boolean =>
  isPriceRangeFields(param.old) ||
  isPriceRangeFields(param.new) ||
  isPriceAmountFields(param.old) ||
  isPriceAmountFields(param.new);

export const hasPriceAmountChanged = (param: Parameter): boolean =>
  isPriceAmountFields(param.old) || isPriceAmountFields(param.new);

const isStatusFields = (obj: any): obj is Status =>
  typeof obj === "object" && "status" in obj;

export const hasStatusChanged = (param: Parameter): boolean =>
  isStatusFields(param.old) || isStatusFields(param.new);

const isRateNameFields = (obj: any): obj is RateName =>
  typeof obj === "object" && ("rateName" in obj || "name" in obj);

export const hasPriceRateNameChanged = (param: Parameter): boolean =>
  isRateNameFields(param.old) || isRateNameFields(param.new);

const isDeliveryTypeFields = (obj: any): obj is DeliveryType =>
  typeof obj === "object" && ("delivery_type" in obj || "deliveryType" in obj);

export const hasDeliveryTypeChanged = (param: Parameter): boolean =>
  isDeliveryTypeFields(param.old) || isDeliveryTypeFields(param.new);

const isRateNameDelTypeFields = (obj: any): obj is RateName =>
  typeof obj === "object" &&
  ("rateName" in obj || "name" in obj) &&
  ("delivery_type" in obj || "deliveryType" in obj);

export const hasPriceRateNameDelTypeChanged = (param: Parameter): boolean =>
  isRateNameDelTypeFields(param.old) || isRateNameDelTypeFields(param.new);

export const didNotChangeNameOrDeliveryType = (parameters: any[]): boolean =>
  parameters.every(
    param =>
      !["rateName", "name", "delivery_type", "deliveryType"].some(
        key =>
          (param.old && key in param.old) || (param.new && key in param.new)
      )
  );

export const isPriceRateUpdate = (obj: any): obj is PriceRateUpdate =>
  typeof obj === "object" &&
  "rateName" in obj &&
  "deliveryType" in obj &&
  "newStatus" in obj &&
  (!("parameters" in obj) || Array.isArray(obj.parameters));

export const getChangedPriceRangeFields = (
  oldVal: Partial<PriceRange>,
  newVal: Partial<PriceRange>
): string[] => {
  const changedFields: string[] = [];

  if (oldVal.maximumOrderPrice !== newVal.maximumOrderPrice) {
    changedFields.push("maximumOrderPrice");
  }

  if (oldVal.minimumOrderPrice !== newVal.minimumOrderPrice) {
    changedFields.push("minimumOrderPrice");
  }

  if (oldVal.priceAmount !== newVal.priceAmount) {
    changedFields.push("priceAmount");
  }

  return changedFields;
};
